import {
  PeriodDirection,
  PeriodFilterValue,
  PeriodPreset,
  periodPresetLabelKey,
  periodPresetsFor,
} from '@/common/periodFilter';
import { useLocale } from '@/providers/LocaleProvider';
import { KLAYO_COLORS } from '@/themes';
import { BaseSelectMenuOption } from '@/types/components/selector.types';
import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { DatePicker } from './DatePicker';
import { SelectMenu } from './SelectMenu';

interface PeriodFilterProps {
  label: string;
  direction: PeriodDirection;
  value: PeriodFilterValue;
  onChange: (value: PeriodFilterValue) => void;
}

export const PeriodFilter = ({ label, direction, value, onChange }: PeriodFilterProps) => {
  const { translate } = useLocale();

  // Build the options once so `value` and `items` share the same object references — SelectMenu uses
  // the option object as the MenuItem value and matches the selection by reference.
  const options = useMemo<BaseSelectMenuOption[]>(
    () =>
      periodPresetsFor(direction).map((preset) => ({
        label: translate(periodPresetLabelKey(preset)),
        value: preset,
      })),
    [direction, translate]
  );

  const selected = useMemo(
    () => options.find((option) => option.value === value.preset) ?? null,
    [options, value.preset]
  );

  const handlePresetChange = (option: BaseSelectMenuOption | null) => {
    const preset = (option?.value as PeriodPreset | undefined) ?? null;
    onChange({
      preset,
      // Custom keeps its dates; any other preset (or clearing) resets them.
      startDate: preset === 'custom' ? value.startDate : null,
      endDate: preset === 'custom' ? value.endDate : null,
    });
  };

  return (
    <Stack spacing={1}>
      <Typography variant='body2' fontWeight={500}>
        {label}
      </Typography>
      <SelectMenu<BaseSelectMenuOption>
        items={options.map((option) => [option.value as string, option])}
        value={selected}
        clearable
        dense
        placeholder={translate('manage.selectPeriod')}
        onChange={(e) => handlePresetChange(e.target.value as BaseSelectMenuOption | null)}
        sx={{ width: '100%' }}
      />

      {value.preset === 'custom' && (
        <Stack direction='row' spacing={1.5} alignItems='stretch'>
          <Box sx={{ flex: 1 }}>
            <DatePicker
              label={translate('manage.startDate')}
              value={value.startDate}
              isCustom={false}
              maxDate={value.endDate ?? undefined}
              onChange={(date) => onChange({ ...value, startDate: date ?? null })}
              sx={{ width: '100%' }}
            />
          </Box>
          {/* Connector aligned to the input row, not the label above it: a spacer matches the field
              label's height, then the "to" is centred against the input's height. */}
          <Stack sx={{ flex: 'none' }}>
            <Box aria-hidden sx={{ height: '25px' }} />
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
              <Typography variant='body1' color={KLAYO_COLORS.GreyGR700}>
                {translate('manage.to')}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ flex: 1 }}>
            <DatePicker
              label={translate('manage.endDate')}
              value={value.endDate}
              isCustom={false}
              minDate={value.startDate ?? undefined}
              onChange={(date) => onChange({ ...value, endDate: date ?? null })}
              sx={{ width: '100%' }}
            />
          </Box>
        </Stack>
      )}
    </Stack>
  );
};
